import {
    AssignmentRow,
    AssignmentStatus,
    CourseContentType,
    CourseStatus,
} from '../types/learning.types';
import { toSydneyDate } from './helpers';

/**
 * Learning module display helpers (LEARNING.md §7.4).
 *
 * Per DESIGN-LANGUAGE.md §3.3, status colour logic lives here as a class-string helper
 * rather than as an inline switch in JSX. Colours are semantic tokens (destructive /
 * amber / emerald / muted), not the prototype's raw hexes.
 *
 * Category colour is DATA, not a status, so it is applied as an inline style by the
 * components instead of living here.
 */

const DESTRUCTIVE = 'text-destructive border border-destructive/30 bg-destructive/5';
const AMBER = 'text-amber-600 border border-amber-300 bg-amber-50';
const EMERALD = 'text-emerald-600 border border-emerald-300 bg-emerald-50';
const NEUTRAL = 'text-muted-foreground border border-border';

/** Course lifecycle: Draft / Active / Archived. */
export function courseStatusPillClass(status: CourseStatus): string {
    switch (status) {
        case 'Active':
            return EMERALD;
        case 'Archived':
            return AMBER;
        case 'Draft':
        default:
            return NEUTRAL;
    }
}

/**
 * Assignment status. `overdue` wins over everything except nothing — an overdue row is
 * red whatever its status, which is how the register reads at a glance.
 */
export function courseAssignmentStatusPillClass(status: AssignmentStatus, overdue = false): string {
    if (overdue) return DESTRUCTIVE;
    switch (status) {
        case 'In Progress':
            return AMBER;
        case 'Completed':
            return EMERALD;
        case 'Assigned':
        default:
            return NEUTRAL;
    }
}

/**
 * Category lifecycle on the Categories page. Archiving only hides a category from the
 * course picker; it is a state, so it gets the same token pair as the course statuses.
 */
export function courseCategoryStatePillClass(archived: boolean): string {
    if (archived) return AMBER;
    return EMERALD;
}

/** Certificate expiry is informational in v1; amber flags it without blocking anything. */
export function certificateExpiryClass(expiryDate: string | null | undefined): string {
    // Dated → amber, indefinite → muted. A helper (not an inline ternary) so the token
    // choice is made once rather than re-decided in every certificate card.
    if (!expiryDate) return 'text-muted-foreground';
    return 'text-amber-600';
}

export function courseContentTypeLabel(type: CourseContentType): string {
    switch (type) {
        case 'SCORM':
            return 'SCORM package';
        case 'PPTX':
            return 'PowerPoint';
        case 'PDF':
        default:
            return 'PDF document';
    }
}

/** "~20 min", or a placeholder when HR has not set a duration. */
export function durationLabel(minutes: number | null | undefined): string {
    if (!minutes) return 'Duration not set';
    return `~${minutes} min`;
}

/** En dash placeholder for empty table cells, per DESIGN-LANGUAGE.md §8.2. */
export function formatLearningDate(value: string | null | undefined): string {
    if (!value) return '–';
    return toSydneyDate(value) || '–';
}

export function assignmentSourceLabel(row: Pick<AssignmentRow, 'enrollmentSource'>): string {
    switch (row.enrollmentSource) {
        case 'Self':
            return 'Self-enrolled';
        case 'Onboarding':
            return 'Onboarding';
        case 'Assigned':
        default:
            return 'Assigned';
    }
}

export function initials(name: string | null | undefined): string {
    if (!name) return '?';
    return name
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

/** Due-date line on a learner card; wording matches the prototype. */
export function learnerDueLabel(row: Pick<AssignmentRow, 'dueDate' | 'overdue' | 'enrollmentSource'>): string {
    if (!row.dueDate) {
        return row.enrollmentSource === 'Self' ? 'No due date (self-enrolled)' : 'No due date';
    }
    return row.overdue ? `Overdue — was due ${formatLearningDate(row.dueDate)}` : `Due ${formatLearningDate(row.dueDate)}`;
}

/** CTA label for a learner card. */
export function learnerCtaLabel(row: Pick<AssignmentRow, 'status'>): string {
    switch (row.status) {
        case 'Completed':
            return 'Review';
        case 'In Progress':
            return 'Continue';
        case 'Assigned':
        default:
            return 'Start';
    }
}
